import { cn } from '@module/lib/utils';
import { cva } from 'class-variance-authority';
import React from 'react';
import Icon from '../icons/Icon';

type NativeProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'rows' | 'children' | 'className'
>;

export type ChatTextAreaProps = NativeProps & {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: () => void;
  minRows?: number;
  maxRows?: number;
  className?: string;
  wrapperClassName?: string;
  textareaClassName?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onEmojiClick?: () => void;
  sendButtonRef?: React.Ref<HTMLButtonElement | null>;
  /**
   * 전송 버튼 활성화 여부 강제 설정 (텍스트 입력 외 조건으로 활성화가 필요할 때 사용)
   */
  isSendable?: boolean;
  /**
   * 전송 버튼 내부에 표시할 커스텀 엘리먼트
   * 제공되지 않으면 기본 ForwardingFilled 아이콘을 사용합니다.
   */
  sendButtonElem?: React.ReactNode;
};

const chatTextAreaContainer = cva('flex w-full items-center gap-3');

const chatTextAreaWrapper = cva(
  'relative flex w-full items-center gap-2 rounded-[6px] border border-system-level3 bg-system-input-bg px-3 py-3 transition-colors focus-within:border-system-black',
  {
    variants: {
      disabled: {
        true: 'bg-system-level4',
        false: '',
      },
    },
  },
);

const chatTextAreaControl = cva(
  'scrollbar-custom w-full resize-none bg-transparent p-0 text-sm text-system-black leading-[20px] outline-none placeholder:text-system-level2',
  {
    variants: {
      disabled: { true: 'cursor-not-allowed text-system-level2', false: '' },
    },
  },
);

export const ChatTextArea = React.forwardRef<HTMLTextAreaElement, ChatTextAreaProps>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      placeholder,
      disabled,
      className,
      wrapperClassName,
      textareaClassName,
      minRows = 1,
      maxRows = 2,
      onKeyDown,
      onEmojiClick,
      sendButtonRef,
      sendButtonElem,
      isSendable,
      ...rest
    },
    forwardedRef,
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    const assignRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    const clampRows = React.useMemo(() => {
      const safeMin = Math.max(1, Math.floor(minRows));
      const safeMax = Math.max(safeMin, Math.floor(maxRows ?? safeMin));
      return { min: safeMin, max: safeMax };
    }, [minRows, maxRows]);

    const syncHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.style.height = 'auto';

      const computed = window.getComputedStyle(textarea);
      const lineHeight =
        parseFloat(computed.lineHeight) ||
        (parseFloat(computed.fontSize) > 0 ? parseFloat(computed.fontSize) * 1.2 : 20);
      const padding =
        parseFloat(computed.paddingTop) +
        parseFloat(computed.paddingBottom) +
        parseFloat(computed.borderTopWidth) +
        parseFloat(computed.borderBottomWidth);

      const minHeight = clampRows.min * lineHeight + (computed.boxSizing === 'border-box' ? 0 : padding);
      const maxHeight = clampRows.max * lineHeight + (computed.boxSizing === 'border-box' ? 0 : padding);

      const nextHeight = Math.min(Math.max(minHeight, textarea.scrollHeight), maxHeight);

      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [clampRows.max, clampRows.min]);

    React.useEffect(() => {
      syncHeight();
    }, [value, syncHeight]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
      onValueChange(event.currentTarget.value);
    };

    const handleSendButtonClick: React.MouseEventHandler<HTMLButtonElement> = () => {
      onSubmit();
    };

    const containerClassName = chatTextAreaContainer();
    const wrapperClass = chatTextAreaWrapper({ disabled: !!disabled });
    const controlClassName = chatTextAreaControl({ disabled: !!disabled });

    return (
      <div className={cn(containerClassName, className)}>
        <div className={cn(wrapperClass, wrapperClassName)}>
          <textarea
            {...rest}
            id='chat-input'
            ref={assignRefs}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            className={cn(controlClassName, textareaClassName)}
            rows={clampRows.min}
          />
          <div className='flex shrink-0 items-center gap-2 self-end'>
            {onEmojiClick && (
              <button type='button' disabled={disabled} onClick={onEmojiClick}>
                <Icon
                  eager
                  name='EmojiBd'
                  className={cn('text-system-level1', {
                    'text-system-level2': disabled,
                  })}
                />
              </button>
            )}
            <button
              id='chat-send-button'
              data-gtm-chat-message-send
              disabled={disabled}
              onClick={handleSendButtonClick}
              ref={sendButtonRef}
              className={cn({
                'font-bold': disabled,
              })}
            >
              {sendButtonElem ?? (
                <Icon
                  eager
                  name='ForwardingFilled'
                  className={cn('text-system-level2', {
                    'text-system-black': (value.length > 0 || isSendable) && !disabled,
                  })}
                />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

ChatTextArea.displayName = 'ChatTextArea';
